import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * Dropdown with filter input (combobox-style). Matches small form controls elsewhere in MRP screens.
 *
 * @param {{ value: string, label: string, buttonLabel?: string, searchText?: string }[]} options
 */
export default function SearchableSelect({
  options,
  value,
  onChange,
  searchPlaceholder = 'Search…',
  emptyLabel = '— Select —',
  allowEmpty = true,
  disabled = false,
  className = '',
  buttonClassName = '',
  listMaxHeight = 'min(220px, 36vh)',
  id,
  usePortal = false,
  menuMinWidth = 0,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState({});
  const rootRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const width = Math.max(rect.width, menuMinWidth || 0, 200);
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: Math.min(rect.left, window.innerWidth - width - 8),
      width,
      zIndex: 10000,
    });
  }, [menuMinWidth]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouse = (e) => {
      const inRoot = rootRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inRoot && !inMenu) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouse);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocMouse);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !usePortal) return undefined;
    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [open, usePortal, updateMenuPosition]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label ?? ''} ${o.searchText ?? ''} ${o.value ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const selected = options.find((o) => String(o.value) === String(value));

  const toggle = () => {
    if (disabled) return;
    setOpen((o) => {
      const next = !o;
      if (next) setQuery('');
      return next;
    });
  };

  const pick = (nextVal) => {
    onChange(nextVal === '' ? '' : String(nextVal));
    setOpen(false);
    setQuery('');
  };

  const baseBtn =
    'w-full min-h-[2rem] px-3 text-xs border rounded bg-white text-left flex items-center justify-between gap-2 ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 disabled:opacity-45 disabled:cursor-not-allowed';

  const menuPanel = (
    <div
      ref={menuRef}
      className={`flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl ring-1 ring-black/5 ${
        usePortal ? '' : 'absolute left-0 right-0 top-full z-[100] mt-1'
      }`}
      style={usePortal ? menuStyle : undefined}
      role="listbox"
    >
      <div className="border-b border-slate-100 p-2 bg-slate-50/90">
        <input
          type="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          aria-label={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setOpen(false);
            }
          }}
          placeholder={searchPlaceholder}
          className="w-full h-8 rounded-md border border-slate-200 bg-white px-2.5 text-xs text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/15"
          autoFocus
        />
      </div>
      <ul className="overflow-y-auto py-1" style={{ maxHeight: listMaxHeight }}>
        {allowEmpty && (
          <li>
            <button
              type="button"
              role="option"
              className={`w-full px-3 py-2 text-left text-xs hover:bg-slate-50 ${
                value === '' || value == null ? 'bg-blue-50 font-semibold text-blue-900' : 'text-slate-700'
              }`}
              onClick={() => pick('')}
            >
              {emptyLabel}
            </button>
          </li>
        )}
        {filtered.map((o) => {
          const isSel = String(o.value) === String(value);
          return (
            <li key={String(o.value)}>
              <button
                type="button"
                role="option"
                aria-selected={isSel}
                className={`w-full px-3 py-2 text-left text-xs hover:bg-slate-50 whitespace-normal break-words ${
                  isSel ? 'bg-blue-50 font-semibold text-blue-900' : 'text-slate-700'
                }`}
                onClick={() => pick(o.value)}
              >
                {o.label}
              </button>
            </li>
          );
        })}
      </ul>
      {filtered.length === 0 && (
        <div className="px-3 py-4 text-center text-xs text-slate-400 border-t border-slate-50">
          No matches
        </div>
      )}
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${baseBtn} ${buttonClassName}`}
      >
        <span className={`truncate min-w-0 ${selected ? 'text-slate-800' : 'text-slate-400'}`}>
          {selected ? (selected.buttonLabel ?? selected.label) : emptyLabel}
        </span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (usePortal ? createPortal(menuPanel, document.body) : menuPanel)}
    </div>
  );
}
